<script lang="ts">
  import { navigate } from "$lib/router.svelte";
  import { auth } from "$lib/stores/auth.svelte";

  // Se já logado, vai direto pro Dashboard
  $effect(() => {
    if (!auth.loading && auth.user) navigate("/dashboard");
  });

  const features = [
    { emoji: "💸", title: "Lançamentos em segundos", desc: "Digite ou mande voz no Telegram. A IA classifica automaticamente." },
    { emoji: "🔄", title: "Recorrências inteligentes", desc: "Assinaturas, parcelas e receitas fixas cadastradas uma vez, geradas para sempre." },
    { emoji: "📊", title: "Painel que faz sentido", desc: "Saldo real de todas as contas, vencimentos próximos e alerta de despesas no mês." },
    { emoji: "🤖", title: "Telegram como interface", desc: "Registre gastos, receba resumo semanal e alertas de vencimento sem abrir o app." },
    { emoji: "🔒", title: "Camada privada com PIN", desc: "Transações sensíveis ficam escondidas com PIN de 4 a 8 dígitos." },
    { emoji: "📤", title: "Exportação e backup", desc: "CSV, OFX e backup automático no OneDrive. Seus dados são seus." },
  ];

  const plans = [
    {
      name: "Pessoal",
      price: "R$ 19,90",
      period: "/mês",
      highlight: false,
      features: ["1 workspace", "Contas e transações ilimitadas", "IA para classificação", "Telegram integrado", "Recorrências e metas"],
    },
    {
      name: "Família",
      price: "R$ 34,90",
      period: "/mês",
      highlight: true,
      badge: "Mais popular",
      features: ["Até 5 membros", "Workspaces separados por pessoa", "Tudo do Pessoal", "Compartilhamento seletivo", "Prioridade no suporte"],
    },
    {
      name: "Negócio",
      price: "R$ 59,90",
      period: "/mês",
      highlight: false,
      features: ["Membros ilimitados", "PF + PJ separados", "Centros de custo", "Fornecedores e cartões", "Exportação OFX + relatórios"],
    },
  ];
</script>

<svelte:head>
  <title>TáKite — Controle financeiro pessoal com IA e Telegram</title>
  <meta name="description" content="Registre gastos por voz no Telegram, acompanhe saldo em tempo real e deixe a IA organizar tudo. Grátis por 30 dias, sem cartão." />
  <meta property="og:title" content="TáKite — Controle financeiro com IA" />
  <meta property="og:description" content="Registre gastos por voz, receba resumos semanais no Telegram e tenha o controle real das suas finanças." />
  <meta property="og:type" content="website" />
</svelte:head>

<!-- Navbar pública -->
<header class="sticky top-0 z-50 border-b backdrop-blur-md" style="background: rgba(var(--bg-rgb, 10 10 15) / 0.92); border-color: var(--border)">
  <div class="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
    <span class="text-xl font-bold text-brand">TáKite</span>
    <nav class="flex items-center gap-3">
      <button class="btn-ghost text-sm" onclick={() => navigate("/login")}>Entrar</button>
      <button class="btn-primary text-sm" onclick={() => navigate("/register")}>Começar grátis</button>
    </nav>
  </div>
</header>

<!-- Hero -->
<section class="max-w-4xl mx-auto px-4 py-20 text-center space-y-6">
  <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border"
       style="border-color: var(--brand); color: var(--brand); background: color-mix(in srgb, var(--brand) 10%, transparent)">
    🎉 30 dias grátis — sem cartão de crédito
  </div>

  <h1 class="text-4xl sm:text-6xl font-extrabold leading-tight tracking-tight">
    Suas finanças em<br />
    <span class="text-brand">ordem de verdade</span>
  </h1>

  <p class="text-lg text-muted max-w-xl mx-auto">
    Registre gastos por voz no Telegram, acompanhe saldo consolidado de todas as contas e deixe a IA classificar tudo. Simples assim.
  </p>

  <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
    <button class="btn-primary text-base px-8 py-3" onclick={() => navigate("/register")}>
      Criar conta grátis →
    </button>
    <button class="btn-ghost text-base px-6 py-3" onclick={() => navigate("/demo")}>
      Ver demo sem cadastro
    </button>
  </div>

  <p class="text-xs text-muted">Já tem conta? <button class="text-brand underline" onclick={() => navigate("/login")}>Entrar</button></p>
</section>

<!-- Mockup / social proof -->
<section class="max-w-5xl mx-auto px-4 pb-16">
  <div class="rounded-2xl overflow-hidden border shadow-2xl" style="border-color: var(--border)">
    <div class="flex items-center gap-1.5 px-4 py-2.5" style="background: var(--surface)">
      <span class="w-3 h-3 rounded-full bg-red-500"></span>
      <span class="w-3 h-3 rounded-full bg-yellow-500"></span>
      <span class="w-3 h-3 rounded-full bg-green-500"></span>
      <span class="ml-2 text-xs text-muted">takite.marcio3d.com.br</span>
    </div>
    <!-- Mini dashboard mockup -->
    <div class="p-6 space-y-4" style="background: var(--bg)">
      <div class="flex items-center justify-between">
        <div>
          <div class="font-bold text-lg">Painel</div>
          <div class="text-xs text-muted">Olá, Maria 👋</div>
        </div>
        <div class="text-xs px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 font-medium">Trial — 28 dias restantes</div>
      </div>
      <div class="grid grid-cols-3 gap-3">
        <div class="rounded-xl p-4" style="background: var(--surface)">
          <div class="text-xs text-muted mb-1">RECEITAS</div>
          <div class="text-xl font-bold text-emerald-500">R$ 5.200,00</div>
        </div>
        <div class="rounded-xl p-4" style="background: var(--surface)">
          <div class="text-xs text-muted mb-1">DESPESAS</div>
          <div class="text-xl font-bold text-red-500">R$ 3.840,00</div>
        </div>
        <div class="rounded-xl p-4" style="background: var(--surface)">
          <div class="text-xs text-muted mb-1">SALDO</div>
          <div class="text-xl font-bold text-emerald-500">R$ 1.360,00</div>
        </div>
      </div>
      <div class="rounded-xl p-4 border" style="background: var(--surface); border-color: var(--border)">
        <div class="text-xs text-muted mb-2">📅 Vencendo nos próximos 7 dias</div>
        <div class="space-y-2">
          {#each [["Netflix", "Amanhã", "R$ 55,90"], ["Aluguel", "Em 3 dias", "R$ 1.800,00"], ["Internet", "Em 5 dias", "R$ 89,90"]] as [desc, when, val]}
            <div class="flex items-center justify-between text-sm">
              <div>
                <span class="font-medium">{desc}</span>
                <span class="text-xs text-muted ml-2">{when}</span>
              </div>
              <span class="font-semibold text-red-500">{val}</span>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Features -->
<section class="max-w-5xl mx-auto px-4 py-16">
  <h2 class="text-2xl sm:text-3xl font-bold text-center mb-10">Tudo que você precisa, nada que você não usa</h2>
  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {#each features as f}
      <div class="rounded-xl p-5 border space-y-2" style="background: var(--surface); border-color: var(--border)">
        <div class="text-3xl">{f.emoji}</div>
        <div class="font-semibold">{f.title}</div>
        <div class="text-sm text-muted">{f.desc}</div>
      </div>
    {/each}
  </div>
</section>

<!-- Telegram highlight -->
<section class="max-w-4xl mx-auto px-4 py-16">
  <div class="rounded-2xl p-8 sm:p-12 text-center space-y-4 border"
       style="background: linear-gradient(135deg, color-mix(in srgb, var(--brand) 8%, transparent), var(--surface)); border-color: var(--brand)">
    <div class="text-5xl">🤖</div>
    <h2 class="text-2xl sm:text-3xl font-bold">Registre pelo Telegram</h2>
    <p class="text-muted max-w-md mx-auto">
      Mande uma mensagem de voz ou texto pro seu bot e o TáKite já lança a transação, classifica e confirma — tudo sem abrir o app.
    </p>
    <div class="flex flex-col items-center gap-2 text-sm">
      <div class="flex items-center gap-2 px-4 py-2 rounded-xl" style="background: var(--bg)">
        <span>🎤</span>
        <span class="text-muted italic">"Gastei 45 reais no almoço"</span>
      </div>
      <div class="text-muted text-xs">↓ IA classifica automaticamente</div>
      <div class="flex items-center gap-2 px-4 py-2 rounded-xl" style="background: var(--bg)">
        <span>✅</span>
        <span>Alimentação · <strong>R$ 45,00</strong> · Confirmar?</span>
      </div>
    </div>
  </div>
</section>

<!-- Planos -->
<section class="max-w-5xl mx-auto px-4 py-16" id="planos">
  <h2 class="text-2xl sm:text-3xl font-bold text-center mb-3">Preços simples e transparentes</h2>
  <p class="text-center text-muted mb-10">30 dias grátis em qualquer plano. Sem cartão de crédito.</p>
  <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
    {#each plans as p}
      <div class="rounded-2xl p-6 border flex flex-col gap-4 relative"
           style="background: var(--surface); border-color: {p.highlight ? 'var(--brand)' : 'var(--border)'};
                  {p.highlight ? 'box-shadow: 0 0 0 1px var(--brand)' : ''}">
        {#if p.badge}
          <div class="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold"
               style="background: var(--brand); color: white">{p.badge}</div>
        {/if}
        <div>
          <div class="font-bold text-lg">{p.name}</div>
          <div class="flex items-baseline gap-1 mt-1">
            <span class="text-3xl font-extrabold">{p.price}</span>
            <span class="text-muted text-sm">{p.period}</span>
          </div>
        </div>
        <ul class="space-y-2 flex-1">
          {#each p.features as feat}
            <li class="flex items-start gap-2 text-sm">
              <span class="text-emerald-500 flex-none mt-0.5">✓</span>
              <span>{feat}</span>
            </li>
          {/each}
        </ul>
        <button class="{p.highlight ? 'btn-primary' : 'btn-ghost border'} w-full"
                style="{!p.highlight ? 'border-color: var(--border)' : ''}"
                onclick={() => navigate("/register")}>
          Começar grátis
        </button>
      </div>
    {/each}
  </div>
</section>

<!-- CTA Final -->
<section class="max-w-2xl mx-auto px-4 py-20 text-center space-y-5">
  <h2 class="text-3xl font-bold">Pronto para ter controle de verdade?</h2>
  <p class="text-muted">Crie sua conta em menos de 1 minuto. Grátis por 30 dias.</p>
  <button class="btn-primary text-base px-10 py-3" onclick={() => navigate("/register")}>
    Criar conta grátis →
  </button>
</section>

<!-- Footer -->
<footer class="border-t py-8 text-center text-xs text-muted" style="border-color: var(--border)">
  <p>© {new Date().getFullYear()} TáKite · Feito no Brasil 🇧🇷</p>
  <p class="mt-1">Seus dados são criptografados e nunca compartilhados.</p>
</footer>
